import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import ReactImageGallery from 'react-image-gallery';
import { FiSearch } from 'react-icons/fi';

import { propTypes } from '../../../util/types';
import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import {
  AspectRatioWrapper,
  Button,
  IconNextArrow,
  IconPrevArrow,
  Modal,
  NamedLink,
  ResponsiveImage,
} from '../../../components';
import { isArrayLength } from '../../../util/genericHelpers';

// Copied directly from
// `node_modules/react-image-gallery/styles/css/image-gallery.css`. The
// copied file is left unedited, and all the overrides are defined in
// the component CSS file below.
import './image-gallery.css';

import css from './ListingImageGallery.module.css';

const IMAGE_GALLERY_OPTIONS = {
  showPlayButton: false,
  disableThumbnailScroll: true,
  showFullscreenButton: false,
};
const NO_OP = () => {};
const MAX_LANDSCAPE_ASPECT_RATIO = 2; // 2:1
const MAX_PORTRAIT_ASPECT_RATIO = 4 / 3;

const getFirstImageAspectRatio = (firstImage, scaledVariant) => {
  if (!firstImage) {
    return { aspectWidth: 1, aspectHeight: 1 };
  }

  const v = firstImage?.attributes?.variants?.[scaledVariant];
  const w = v?.width;
  const h = v?.height;
  const hasDimensions = !!w && !!h;
  const aspectRatio = w / h;

  // We keep the fractions separated as these are given to AspectRatioWrapper
  // which expects separate width and height
  return hasDimensions && aspectRatio >= MAX_LANDSCAPE_ASPECT_RATIO
    ? { aspectWidth: 2, aspectHeight: 1 }
    : hasDimensions && aspectRatio <= MAX_PORTRAIT_ASPECT_RATIO
    ? { aspectWidth: 4, aspectHeight: 3 }
    : hasDimensions
    ? { aspectWidth: w, aspectHeight: h }
    : { aspectWidth: 1, aspectHeight: 1 };
};

/**
 * The ListingImageGallery component.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {Array<propTypes.image>} props.images - The images
 * @param {Array<string>} props.imageVariants - The image variants
 * @param {Array<string>} props.thumbnailVariants - The thumbnail variants
 * @returns {JSX.Element} listing image gallery component
 */
const ListingImageGallery = ({
  rootClassName = null,
  className = null,
  images = [],
  imageVariants,
  thumbnailVariants = null,
  onManageDisableScrolling = NO_OP,
  soldByName = null,
  soldByLinkProps = null,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [modalStartIndex, setModalStartIndex] = useState(0);
  const [modalSessionId, setModalSessionId] = useState(0);
  const [isZoomMode, setIsZoomMode] = useState(false);
  const intl = useIntl();
  const thumbVariants = thumbnailVariants || imageVariants;
  const safeImages = Array.isArray(images) ? images : [];
  const zoomInLabel = intl.formatMessage({
    id: 'ListingImageGallery.zoomInAriaLabel',
    defaultMessage: 'Zoom in on image',
  });
  const modalZoomToggleLabel = intl.formatMessage({
    id: 'ListingImageGallery.modalZoomToggleLabel',
    defaultMessage: 'Zoom',
  });
  const modalZoomEnabledLabel = intl.formatMessage({
    id: 'ListingImageGallery.modalZoomEnabledLabel',
    defaultMessage: 'Zoom on',
  });
  const modalZoomDisabledLabel = intl.formatMessage({
    id: 'ListingImageGallery.modalZoomDisabledLabel',
    defaultMessage: 'Zoom off',
  });

  // imageVariants are scaled variants.
  const { aspectWidth, aspectHeight } = getFirstImageAspectRatio(safeImages?.[0], imageVariants[0]);
  const items = useMemo(
    () =>
      safeImages.map((img, i) => ({
        // ReactImageGallery requires `original` key for each item.
        original: '',
        index: i,
        alt: intl.formatMessage(
          { id: 'ListingImageGallery.imageAltText' },
          { index: i + 1, count: safeImages.length }
        ),
        thumbAlt: intl.formatMessage(
          { id: 'ListingImageGallery.imageThumbnailAltText' },
          { index: i + 1, count: safeImages.length }
        ),
        thumbnail: img?.attributes?.variants?.[thumbVariants[0]],
        image: img,
      })),
    [safeImages, intl, thumbVariants]
  );

  const openModal = (index, { enableZoom = false } = {}) => {
    const nextIndex = Number.isInteger(index) ? index : activeIndex;
    setActiveIndex(nextIndex);
    setModalStartIndex(nextIndex);
    setModalSessionId(session => session + 1);
    setIsZoomMode(enableZoom);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setIsZoomMode(false);
  };

  const handleToggleZoomMode = event => {
    event?.stopPropagation?.();
    setIsZoomMode(currentValue => !currentValue);
  };

  const renderImage = (item, isModal = false) => {
    const handleOpenModal = event => {
      event?.stopPropagation?.();
      openModal(item.index);
    };

    return (
      <AspectRatioWrapper
        width={aspectWidth || 1}
        height={aspectHeight || 1}
        className={isModal ? css.itemWrapperFullscreen : css.itemWrapper}
      >
        <div className={css.itemCentering}>
          <ResponsiveImage
            rootClassName={classNames(css.item, {
              [css.clickableItem]: !isModal,
            })}
            image={item.image}
            alt={item.alt}
            variants={imageVariants}
            showAll={isModal && isZoomMode}
            sizes={`(max-width: 1024px) 100vw, (max-width: 1200px) calc(100vw - 192px), 708px`}
            onClick={!isModal ? handleOpenModal : undefined}
          />
        </div>
      </AspectRatioWrapper>
    );
  };

  const renderThumbInner = item => {
    return (
      <div>
        <ResponsiveImage
          rootClassName={css.thumb}
          image={item.image}
          alt={item.thumbAlt}
          variants={thumbVariants}
          sizes="88px"
        />
      </div>
    );
  };

  const renderLeftNav = (onClick, disabled) => {
    return (
      <button type="button" className={css.navLeft} disabled={disabled} onClick={onClick}>
        <IconPrevArrow className={css.arrowIcon} />
      </button>
    );
  };

  const renderRightNav = (onClick, disabled) => {
    return (
      <button type="button" className={css.navRight} disabled={disabled} onClick={onClick}>
        <IconNextArrow className={css.arrowIcon} />
      </button>
    );
  };

  const renderModalZoomToggle = () => {
    return (
      <div className={css.modalZoomControls}>
        <button
          type="button"
          className={classNames(css.modalZoomToggleButton, {
            [css.modalZoomToggleButtonActive]: isZoomMode,
          })}
          onClick={handleToggleZoomMode}
          aria-label={modalZoomToggleLabel}
          aria-pressed={isZoomMode}
          title={modalZoomToggleLabel}
        >
          <FiSearch className={css.modalZoomToggleIcon} aria-hidden="true" />
          <span className={css.modalZoomToggleText}>
            {isZoomMode ? modalZoomEnabledLabel : modalZoomDisabledLabel}
          </span>
        </button>
      </div>
    );
  };

  if (items.length === 0) {
    return <ResponsiveImage className={css.noImage} image={null} variants={[]} alt="" />;
  }

  const showThumbnails = isArrayLength(items) && items.length > 1;
  const classes = classNames(rootClassName || css.root, className, css.listingImageGallarySlider, {
    [css.thumbSlider]: showThumbnails,
  });

  const soldByLabel = soldByName ? (
    <div
      className={css.soldByOverlay}
      onClick={event => {
        event.stopPropagation();
      }}
    >
      <FormattedMessage
        id="ListingPage.soldBy"
        values={{
          name: soldByLinkProps ? (
            <NamedLink className={css.soldByLink} {...soldByLinkProps}>
              {soldByName}
            </NamedLink>
          ) : (
            soldByName
          ),
        }}
      />
    </div>
  ) : null;

  return (
    <div className={classes}>
      {soldByLabel}
      <ReactImageGallery
        additionalClass={classNames(css.gallery, {
          [css.galleryWithThumbs]: showThumbnails,
        })}
        items={items}
        startIndex={activeIndex}
        onSlide={setActiveIndex}
        renderItem={item => renderImage(item, false)}
        renderThumbInner={renderThumbInner}
        renderLeftNav={renderLeftNav}
        renderRightNav={renderRightNav}
        showThumbnails={showThumbnails}
        {...IMAGE_GALLERY_OPTIONS}
      />

      <div
        className={css.viewPhotos}
        onClick={event => {
          event.stopPropagation();
        }}
      >
        <button
          type="button"
          className={css.zoomActionButton}
          onClick={() => openModal(activeIndex, { enableZoom: true })}
          aria-label={zoomInLabel}
          title={zoomInLabel}
        >
          <FiSearch className={css.zoomButtonIcon} aria-hidden="true" />
        </button>
        <Button
          onClick={() => openModal(activeIndex)}
          rootClassName={css.viewPhotosButton}
          aria-label={intl.formatMessage({ id: 'ListingImageGallery.showAllPhotosAriaLabel' })}
          title={intl.formatMessage({ id: 'ListingImageGallery.showAllPhotosAriaLabel' })}
        >
          <FormattedMessage
            id="ListingImageGallery.showAll"
            values={{ count: safeImages.length }}
          />
        </Button>
      </div>

      <Modal
        id="ListingImageGalleryModal"
        modalTitle={intl.formatMessage(
          { id: 'ListingImageGallery.modalTitle' },
          { count: safeImages.length }
        )}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onManageDisableScrolling={onManageDisableScrolling}
        contentClassName={css.modalContent}
        usePortal
      >
        <ReactImageGallery
          key={`modal-gallery-${modalSessionId}`}
          additionalClass={classNames(css.gallery, css.modalGallery)}
          items={items}
          startIndex={modalStartIndex}
          onSlide={setActiveIndex}
          renderItem={item => renderImage(item, true)}
          renderThumbInner={renderThumbInner}
          renderLeftNav={renderLeftNav}
          renderRightNav={renderRightNav}
          renderCustomControls={renderModalZoomToggle}
          showThumbnails={showThumbnails}
          {...IMAGE_GALLERY_OPTIONS}
        />
      </Modal>
    </div>
  );
};

const { string, arrayOf, func } = PropTypes;

ListingImageGallery.propTypes = {
  rootClassName: string,
  className: string,
  images: arrayOf(propTypes.image).isRequired,
  imageVariants: arrayOf(string).isRequired,
  thumbnailVariants: arrayOf(string),
  onManageDisableScrolling: func,
  soldByName: string,
  soldByLinkProps: PropTypes.object,
};

export default ListingImageGallery;
